import { useMemo } from "react";
import { StyleSheet, Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import {
  buildCursorPath,
  effortCoverage,
  trailingRange,
  type Exercise,
  type TimeRange,
} from "@heraion/shared";

import { ChartCappedNote } from "../charts/ChartCappedNote";
import { Card } from "../primitives/Card";
import { LoadingIndicator } from "../primitives/LoadingIndicator";
import { StatPair } from "../primitives/StatPair";
import { NO_COVERAGE_GLYPH, noCoverageWord, TargetBar } from "../primitives/TargetBar";
import { DEFAULT_WINDOW_DAYS } from "../Progress/progressWindow";
import type { ProgressApiFetch } from "../Progress/types";
import { useWindowSets } from "../Progress/useWindowSets";
import type { UiTheme } from "../theme";

// <EffortCoveragePanel> (CO-10) — how much of the athlete's logged work
// carries a recorded RIR. The coach's real question is whether the effort-
// driven layer next to it (hard sets, targets) rests on real data or on a
// handful of tagged sets.
//
// Deliberately NO zone and NO target: a coverage number has no prescribed
// goal, and picking a "good coverage" threshold would be JL judging the
// athlete. The platform rule is to EXPOSE the value and let the coach read
// it — same posture as the health metrics. So the bar is the neutral
// (untargeted) TargetBar track, and the panel states the raw pair behind the
// percentage instead of grading it.
//
// The math (window, denominator, the effort-era boundary) lives in shared's
// effortCoverage — see its header for the two decisions it encodes.

// The athlete's sets over the standard window, through the ONE coach-gated
// route that serves them: /api/sets is self-only, /api/sessions?include=sets
// is what resolveCoachReadTarget gates. Window mechanics (cancel, safeParse,
// since) come from the shared useWindowSets — this names the route and the
// envelope, nothing else.
const useAthleteSets = (apiFetch: ProgressApiFetch, range: TimeRange) =>
  useWindowSets(
    apiFetch,
    range,
    ({ sinceIso, untilIso, cursor }) =>
      buildCursorPath(
        "/api/sessions",
        `include=sets&since=${encodeURIComponent(sinceIso ?? "")}&until=${encodeURIComponent(untilIso ?? "")}&limit=200`,
        cursor,
      ),
    (resp) =>
      ((resp as { items?: Array<{ sets?: unknown[] }> }).items ?? []).flatMap(
        (s) => s.sets ?? [],
      ),
  );

export type EffortCoveragePanelProps = {
  theme: UiTheme;
  /** Athlete-scoped fetch (makeAthleteScopedApiFetch) — appends ?athleteSub. */
  apiFetch: ProgressApiFetch;
  /** The athlete's catalog, already loaded by the detail screen; null while
   *  it loads. Passed in rather than re-fetched so the panel adds ONE request
   *  to the surface, not two. */
  catalog: ReadonlyArray<Exercise> | null;
};

export function EffortCoveragePanel({
  theme,
  apiFetch,
  catalog,
}: EffortCoveragePanelProps) {
  const { t, i18n } = useTranslation();
  const styles = useMemo(() => makeStyles(theme), [theme]);
  // Stable across renders (computed once), or the load refetches.
  const range = useMemo(() => trailingRange(Date.now(), DEFAULT_WINDOW_DAYS), []);
  const { sets, error, capped } = useAthleteSets(apiFetch, range);
  const dateFmt = useMemo(
    () =>
      new Intl.DateTimeFormat(i18n.language, {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }),
    [i18n.language],
  );
  const cov = useMemo(
    () => (sets && catalog ? effortCoverage(sets, catalog) : null),
    [sets, catalog],
  );

  return (
    <Card theme={theme}>
      <Text style={styles.cardTitle} accessibilityRole="header">
        {t("coach.detail.effortTitle")}
      </Text>
      {error ? (
        <Text style={styles.cardBody}>{t("coach.detail.effortError")}</Text>
      ) : !cov ? (
        <LoadingIndicator theme={theme} />
      ) : cov.pct == null ? (
        // Erika's case: the athlete has never tagged a set. "no coverage" is
        // a different statement from 0% and the meter's fourth state exists
        // exactly for it — glyph + word, never colour alone. One composed
        // label, the same idiom TargetMeterRow uses for this glyph/word pair,
        // so a screen reader hears "Effort coverage, no coverage" instead of a
        // bare "∅" followed by a disconnected word.
        <>
          <View
            accessible
            accessibilityLabel={`${t("coach.detail.effortTitle")}, ${noCoverageWord(t)}`}
          >
            <Text style={styles.noCoverage}>
              {NO_COVERAGE_GLYPH} {noCoverageWord(t)}
            </Text>
            <Text style={styles.cardBody}>{t("coach.detail.effortNone")}</Text>
          </View>
          {/* #122: older, unread data may carry tags; "never tagged" is only
              true of the window actually read. */}
          {capped ? <ChartCappedNote theme={theme} /> : null}
        </>
      ) : (
        <>
          <StatPair
            theme={theme}
            value={`${cov.pct}%`}
            label={t("coach.detail.effortLabel")}
            labelLines={2}
            sample={t("mobile.logger.totalsSets", { count: cov.counted })}
          />
          <View style={styles.bar}>
            <TargetBar
              theme={theme}
              actual={cov.pct}
              target={null}
              zone={null}
              degradedMax={100}
            />
          </View>
          <Text style={styles.cardBody}>
            {t("coach.detail.effortSummary", {
              tagged: cov.tagged,
              counted: cov.counted,
            })}
          </Text>
          {/* Only when the window actually straddles the athlete's first
              tagged set — otherwise the date would be the fetch window's
              edge dressed up as a fact about the athlete. */}
          {cov.eraStart && cov.preEraCounted > 0 ? (
            <Text style={styles.era}>
              {t("coach.detail.effortEra", {
                date: dateFmt.format(new Date(cov.eraStart)),
                count: cov.preEraCounted,
              })}
            </Text>
          ) : null}
          {capped ? <ChartCappedNote theme={theme} /> : null}
        </>
      )}
    </Card>
  );
}

// Title/body deliberately mirror AthleteDetail's cardTitle/cardBody TOKEN FOR
// TOKEN — this card sits in the same grid as the ones styled there, so the two
// must not drift. A third Coach panel with this "title + body in a bare Card"
// idiom should collapse them into a shared style (CN-coach-panel-style in
// next/consolidation.md), the way Progress has tierTwoPanelStyles.
const makeStyles = (theme: UiTheme) => {
  const { palette: p, spacing: s, typography: ty } = theme;
  return StyleSheet.create({
    cardTitle: {
      color: p.ink,
      fontSize: ty.sizes.md,
      fontWeight: "700",
      marginBottom: s.s2,
    },
    cardBody: { color: p.ink2, fontSize: ty.sizes.sm },
    noCoverage: {
      color: p.ink2,
      fontSize: ty.sizes.lg,
      fontWeight: "700",
      marginBottom: s.s1,
    },
    bar: { marginTop: s.s3, marginBottom: s.s3 },
    era: { color: p.ink3, fontSize: ty.sizes.sm, marginTop: s.s2 },
  });
};
